import { useRef, useState } from 'react';
import { DEMO_GAME_PGN } from '../../data/demoGame';
import { importPgnText, parseSingleGame } from '../../lib/pgn/parse';
import type { Color, ParsedGame } from '../../types';

export interface ImportPanelProps {
  onGameReady: (game: ParsedGame, color: Color, pgnRaw: string) => void;
}

type Stage = 'input' | 'choose-game' | 'choose-color';

export function ImportPanel({ onGameReady }: ImportPanelProps) {
  const [pgnText, setPgnText] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<ParsedGame[]>([]);
  const [chosenGame, setChosenGame] = useState<ParsedGame | null>(null);
  const [chosenColor, setChosenColor] = useState<Color>('w');
  const [stage, setStage] = useState<Stage>('input');
  const [rawForChosen, setRawForChosen] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  function runImport(text: string) {
    const result = importPgnText(text);
    setWarnings(result.warnings);
    if (result.games.length === 0) {
      setCandidates([]);
      return;
    }
    if (result.games.length === 1) {
      setChosenGame(result.games[0]);
      setRawForChosen(text);
      setStage('choose-color');
    } else {
      setCandidates(result.games);
      setRawForChosen(text);
      setStage('choose-game');
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pgnText.trim()) {
      setWarnings(['Colle un PGN ou importe un fichier avant de continuer.']);
      return;
    }
    runImport(pgnText);
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pgn') && file.type && !file.type.includes('text')) {
      setWarnings([`Le fichier « ${file.name} » ne ressemble pas à un fichier .pgn.`]);
      return;
    }
    const text = await file.text();
    setPgnText(text);
    runImport(text);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function loadDemo() {
    const game = parseSingleGame(DEMO_GAME_PGN, 0, true);
    setChosenGame(game);
    setRawForChosen(DEMO_GAME_PGN);
    setWarnings([]);
    setChosenColor('b');
    setStage('choose-color');
  }

  function confirmColor() {
    if (!chosenGame) return;
    onGameReady(chosenGame, chosenColor, rawForChosen);
  }

  function backToInput() {
    setStage('input');
    setCandidates([]);
    setChosenGame(null);
  }

  if (stage === 'choose-game') {
    return (
      <section className="panel">
        <h2>Plusieurs parties détectées</h2>
        <p className="muted">
          Ce fichier contient {candidates.length} parties. Magnus analyse une partie à la fois en V1 : choisis
          celle à étudier.
        </p>
        <ul className="game-candidate-list">
          {candidates.map((g, i) => (
            <li key={g.id}>
              <button
                type="button"
                className="game-candidate"
                onClick={() => {
                  setChosenGame(g);
                  setStage('choose-color');
                }}
              >
                <strong>
                  {i + 1}. {g.headers.White ?? '?'} vs {g.headers.Black ?? '?'}
                </strong>
                <span className="muted">
                  {g.headers.Date ?? 'Date inconnue'} · {g.result} · {g.moves.length} demi-coups
                </span>
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="link-btn" onClick={backToInput}>
          ← Revenir au collage du PGN
        </button>
      </section>
    );
  }

  if (stage === 'choose-color' && chosenGame) {
    return (
      <section className="panel">
        <h2>Quelle couleur as-tu jouée ?</h2>
        <p className="muted">
          {chosenGame.headers.White ?? '?'} (Blancs) vs {chosenGame.headers.Black ?? '?'} (Noirs) —{' '}
          {chosenGame.result}
          {chosenGame.isDemo && ' · Partie de démonstration'}
        </p>
        <div className="color-choice" role="radiogroup" aria-label="Couleur jouée">
          <label>
            <input
              type="radio"
              name="color"
              checked={chosenColor === 'w'}
              onChange={() => setChosenColor('w')}
            />
            Blancs
          </label>
          <label>
            <input
              type="radio"
              name="color"
              checked={chosenColor === 'b'}
              onChange={() => setChosenColor('b')}
            />
            Noirs
          </label>
        </div>
        <div className="actions">
          <button type="button" className="primary-btn" onClick={confirmColor}>
            Commencer l'analyse de cette partie
          </button>
          <button type="button" className="link-btn" onClick={backToInput}>
            ← Choisir une autre partie
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>Importer une partie</h2>
      <form onSubmit={handleSubmit} className="import-form">
        <label htmlFor="pgn-input">Coller le PGN</label>
        <textarea
          id="pgn-input"
          value={pgnText}
          onChange={(e) => setPgnText(e.target.value)}
          rows={8}
          placeholder="[Event &quot;...&quot;]&#10;1. e4 e5 2. Nf3 ..."
        />
        <div className="actions">
          <button type="submit" className="primary-btn">
            Analyser ce PGN
          </button>
          <label className="file-btn">
            Importer un fichier .pgn
            <input ref={fileInputRef} type="file" accept=".pgn,text/plain" onChange={handleFile} hidden />
          </label>
          <button type="button" className="link-btn" onClick={loadDemo}>
            Essayer avec la partie de démonstration
          </button>
        </div>
      </form>
      {warnings.length > 0 && (
        <ul className="warning-list" role="alert">
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
